/**
 * Convert a brush stroke (polyline + radius) to a closed polygon (DESIGN.md brush output).
 */

import type { Point } from './types';

/** Catmull-Rom spline tension. 0.5 = standard; lower = tighter, higher = looser. */
const SPLINE_TENSION = 0.5;

/**
 * Sample a point on a Catmull-Rom spline between p1 and p2, with p0 and p3 as control points.
 * t in [0, 1].
 */
function catmullRom(p0: Point, p1: Point, p2: Point, p3: Point, t: number): Point {
  const t2 = t * t;
  const t3 = t2 * t;
  const x =
    0.5 *
    (2 * p1[0] +
      (-p0[0] + p2[0]) * t * SPLINE_TENSION +
      (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 +
      (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3);
  const y =
    0.5 *
    (2 * p1[1] +
      (-p0[1] + p2[1]) * t * SPLINE_TENSION +
      (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 +
      (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3);
  return [x, y];
}

/**
 * Smooth a polyline using Catmull-Rom spline interpolation.
 * @param points Input points
 * @param closed If true, treats path as cyclic (smooth end-to-start connection)
 * @param segmentsPerEdge Number of interpolated points per segment (default 8)
 */
export function smoothPolyline(
  points: Point[],
  closed: boolean,
  segmentsPerEdge = 8
): Point[] {
  if (points.length < 2) return [...points];
  if (points.length === 2 && !closed) return [...points];

  const n = points.length;
  const out: Point[] = [];

  const get = (i: number): Point => {
    if (closed) {
      const j = ((i % n) + n) % n;
      return points[j]!;
    }
    if (i < 0) return points[0]!;
    if (i >= n) return points[n - 1]!;
    return points[i]!;
  };

  const count = closed ? n : n - 1;
  for (let i = 0; i < count; i++) {
    const p0 = get(i - 1);
    const p1 = get(i);
    const p2 = get(i + 1);
    const p3 = get(i + 2);
    for (let k = 1; k <= segmentsPerEdge; k++) {
      const t = k / segmentsPerEdge;
      out.push(catmullRom(p0, p1, p2, p3, t));
    }
  }
  return out;
}

/**
 * Ray-casting point-in-polygon test. Returns true if [x,y] is inside the polygon.
 */
export function pointInPolygon(point: Point, polygon: Point[]): boolean {
  const [x, y] = point;
  let inside = false;
  const n = polygon.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const pi = polygon[i];
    const pj = polygon[j];
    if (!pi || !pj) continue;
    const xi = pi[0];
    const yi = pi[1];
    const xj = pj[0];
    const yj = pj[1];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function perp(dx: number, dy: number, outward: number): [number, number] {
  const len = Math.hypot(dx, dy) || 1;
  const nx = (-dy / len) * outward;
  const ny = (dx / len) * outward;
  return [nx, ny];
}

/**
 * Build a closed polygon by offsetting the stroke path by halfWidth on both sides.
 * Points are in mm. Returns polygon as array of [x,y] in mm.
 */
export function strokeToPolygon(points: Point[], halfWidthMm: number): Point[] {
  if (points.length < 2) {
    if (points.length === 1) {
      const p0 = points[0];
      if (!p0) return [];
      const [x, y] = p0;
      const h = halfWidthMm;
      return [
        [x - h, y - h],
        [x + h, y - h],
        [x + h, y + h],
        [x - h, y + h],
      ];
    }
    return [];
  }

  const left: Point[] = [];
  const right: Point[] = [];

  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const prev = points[Math.max(0, i - 1)];
    const next = points[Math.min(points.length - 1, i + 1)];
    if (!p || !prev || !next) continue;
    const dx1 = p[0] - prev[0];
    const dy1 = p[1] - prev[1];
    const dx2 = next[0] - p[0];
    const dy2 = next[1] - p[1];
    const dx = dx1 + dx2;
    const dy = dy1 + dy2;
    const [nx, ny] = perp(dx, dy, halfWidthMm);
    left.push([p[0] - nx, p[1] - ny]);
    right.push([p[0] + nx, p[1] + ny]);
  }

  return [...left, ...right.reverse()];
}

/**
 * Signed area (doubled). Positive = CCW, negative = CW (y-up).
 */
function signedArea2(points: Point[]): number {
  let a = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const p = points[i];
    const prev = points[(i - 1 + n) % n];
    const next = points[(i + 1) % n];
    if (!p || !prev || !next) continue;
    a += p[0] * (next[1] - prev[1]);
  }
  return a;
}

/**
 * Offset a closed polygon outward by offsetMm. Outward = to the left of each edge when traversing (CCW).
 * If polygon is CW, negates offset so result is still outside the shape. Used for e.g. 1-yard fringe around greens.
 */
export function polygonOffsetOutward(points: Point[], offsetMm: number): Point[] {
  const n = points.length;
  if (n < 3) return [];
  const offset = signedArea2(points) >= 0 ? offsetMm : -offsetMm;
  const out: Point[] = [];
  for (let i = 0; i < n; i++) {
    const prev = points[(i - 1 + n) % n];
    const curr = points[i];
    const next = points[(i + 1) % n];
    if (!prev || !curr || !next) continue;
    const dx1 = curr[0] - prev[0];
    const dy1 = curr[1] - prev[1];
    const dx2 = next[0] - curr[0];
    const dy2 = next[1] - curr[1];
    const len1 = Math.hypot(dx1, dy1) || 1;
    const len2 = Math.hypot(dx2, dy2) || 1;
    const n1x = -dy1 / len1;
    const n1y = dx1 / len1;
    const n2x = -dy2 / len2;
    const n2y = dx2 / len2;
    const dot = n1x * n2x + n1y * n2y;
    const bx = n1x + n2x;
    const by = n1y + n2y;
    const blen = Math.hypot(bx, by);
    let dist = offset;
    if (blen > 1e-6) {
      const sinHalf = Math.sqrt(Math.max(0, (1 - dot) / 2));
      if (sinHalf > 1e-6) dist = offset / sinHalf;
    }
    const scale = dist / (blen || 1);
    out.push([curr[0] + bx * scale, curr[1] + by * scale]);
  }
  return out;
}

/**
 * Cross product (z) of (p1 - p0) and (p2 - p0). Positive = left turn, negative = right turn.
 */
function cross(o: Point, a: Point, b: Point): number {
  return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
}

/**
 * Convex hull of points (Graham scan). Returns points in counter-clockwise order.
 * Fills the inner contents when the stroke outlines a convex area.
 */
export function convexHull(points: Point[]): Point[] {
  if (points.length < 3) return points.length ? [...points] : [];

  const pts = points.filter((p): p is Point => p != null);
  if (pts.length < 3) return pts;

  // Lowest point (smallest y, then smallest x)
  let start = 0;
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i];
    const s = pts[start];
    if (!p || !s) continue;
    if (p[1] < s[1] || (p[1] === s[1] && p[0] < s[0])) start = i;
  }

  const pivot = pts[start];
  if (!pivot) return pts;

  const rest = pts
    .map((p, i) => (i === start ? null : p))
    .filter((p): p is Point => p != null)
    .sort((a, b) => {
      const ax = a[0] - pivot[0];
      const ay = a[1] - pivot[1];
      const bx = b[0] - pivot[0];
      const by = b[1] - pivot[1];
      const c = cross(pivot, a, b);
      if (c !== 0) return -c; // CCW: larger angle first
      return ax * ax + ay * ay - (bx * bx + by * by); // closer first
    });

  const hull: Point[] = [pivot];
  for (const p of rest) {
    while (hull.length >= 2 && cross(hull[hull.length - 2]!, hull[hull.length - 1]!, p) <= 0) {
      hull.pop();
    }
    hull.push(p);
  }
  return hull;
}
